<template>
  <a-modal
    :open="!!state.rows"
    :footer="null"
    :mask-closable="false"
    :width="720"
    @cancel="review.later"
  >
    <template #title>
      <div class="modal-title">{{ TEXT.title }}</div>
    </template>
    <div class="modal-content security-modal">
      <p class="review-guide">{{ TEXT.guide }}</p>
      <div class="review-list">
        <table class="review-table">
          <thead>
            <tr>
              <th>이름</th>
              <th>아이디</th>
              <th>마지막 로그인</th>
              <th>켜진 메뉴</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in state.rows || []" :key="r.accountId">
              <td>{{ r.name }}</td>
              <td>{{ r.username }}</td>
              <td>
                {{ r.lastLogin }}
                <a-tag v-if="r.inactive90" color="orange">{{
                  TEXT.inactive
                }}</a-tag>
              </td>
              <td>{{ r.menus }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="modal-footer">
        <a-button :disabled="state.busy" @click="review.later">{{
          TEXT.later
        }}</a-button>
        <a-button type="primary" :loading="state.busy" @click="review.confirm">
          {{ TEXT.confirm }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import apiInstance from '@/axios';
import {
  LEGACY_KEYS,
  SUPPORT_KEYS,
  getAccessToken,
  isSupportTab,
} from '@/utils/tokenStore';
import {
  PERM_REVIEW_STATUS_URL,
  PERM_REVIEW_TEXT,
  PERM_REVIEW_URL,
  createPermReview,
} from '@/utils/permReview';

// 사용자가 누르지 않은 조회·기록 — 실패해도 알림·step-up 창 없이 조용히
const QUIET = { showErrorNotice: false, skipStepUp: true };

/**
 * 직원 권한 정기 확인 창(T-CONTRACT-1031 §2). App.vue 에 하나 — active(로그인 뒤 화면)일 때 탭·계정마다 한 번 묻는다.
 * 흐름(세션 바뀜·늦은 답·다른 탭 로그인)은 utils/permReview.js 의 createPermReview. 여기는 버튼·이동·storage 이벤트를 넘기기만.
 */
export default defineComponent({
  name: 'PermReviewModal',
  props: {
    active: { type: Boolean, default: false },
  },
  setup(props) {
    const state = reactive({
      rows: null as Array<{ accountId: string; [key: string]: unknown }> | null,
      busy: false,
    });
    const review = createPermReview({
      state,
      storage: sessionStorage,
      readToken: getAccessToken,
      isActive: () => props.active,
      isSupportTab,
      get: async () =>
        (await apiInstance.get(PERM_REVIEW_STATUS_URL, QUIET)).data,
      post: (body: { accountIds: string[] }) =>
        apiInstance.post(PERM_REVIEW_URL, body, QUIET),
      tokenKeys: [
        LEGACY_KEYS.access,
        SUPPORT_KEYS.access,
        SUPPORT_KEYS.session,
      ],
    });
    return { TEXT: PERM_REVIEW_TEXT, state, review };
  },
  watch: {
    active: { handler: 'check', immediate: true },
    $route: 'check',
  },
  mounted() {
    // 다른 탭의 로그인·로그아웃(토큰·지원 키 바뀜) — 토큰 주인이 바뀌었으면 창을 닫는다
    window.addEventListener('storage', this.review.onStorage);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.review.onStorage);
  },
  methods: {
    check() {
      this.review.check();
    },
  },
});
</script>

<style scoped>
.review-guide {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #4b5158;
}
.review-list {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}
.review-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.review-table th,
.review-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
  vertical-align: top;
}
/* 이름·아이디·마지막 로그인은 한 줄로(좁으면 「정다운」이 한 글자씩 꺾임, 10-31 intest I14) */
.review-table th:nth-child(-n + 3),
.review-table td:nth-child(-n + 3) {
  white-space: nowrap;
}
.review-table th {
  position: sticky;
  top: 0;
  background: #fafafa;
  font-weight: 600;
  color: #333;
}
</style>
